import React from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import {
 FiArrowLeft,
 FiDownload,
 FiEye,
 FiFileText,
 FiInfo,
 FiLock,
 FiEdit3,
} from 'react-icons/fi';
import { AUDIT, AUDIT_LABEL } from '../../theme/auditTokens';
import StatusTag from '../../components/Tags/StatusTag';

const TYPE_LABELS = {
 simple: 'Simples',
 diagnostic: 'Diagnóstico',
 periodic: 'Periódico',
 certification: 'Certificação',
};

// Barra fina de progresso rotulada — "12/18 respondidas" com um trilho embaixo.
function Meter({ label, value, total, color = AUDIT.accent }) {
 const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
 return (
  <Box minW='132px'>
   <Flex justify='space-between' align='baseline' mb='4px' gap='8px'>
    <Text sx={AUDIT_LABEL}>{label}</Text>
    <Text fontSize='12px' fontWeight={700} color={AUDIT.ink} sx={{ fontVariantNumeric: 'tabular-nums' }}>
     {value}
     <Text as='span' color={AUDIT.inkFaint} fontWeight={600}>
      /{total}
     </Text>
    </Text>
   </Flex>
   <Box h='4px' bg={AUDIT.line} borderRadius='999px' overflow='hidden'>
    <Box h='100%' w={`${pct}%`} bg={color} borderRadius='999px' transition='width .25s ease' />
   </Box>
  </Box>
 );
}

function Banner({ icon: Icon, tone, children }) {
 const map = {
  info: { bg: AUDIT.accentSoft, fg: AUDIT.accentInk },
  attention: { bg: '#FBF1E6', fg: '#95571B' },
 };
 const c = map[tone] || map.info;
 return (
  <Flex
   align='center'
   gap='8px'
   mt='12px'
   px='12px'
   py='9px'
   bg={c.bg}
   color={c.fg}
   borderRadius={AUDIT.radiusSm}
   fontSize='13px'
   lineHeight='1.4'
  >
   <Box as={Icon} flexShrink={0} boxSize='15px' />
   <Text>{children}</Text>
  </Flex>
 );
}

const actionBtn = {
 variant: 'ghost',
 size: 'sm',
 h: '34px',
 px: '10px',
 fontSize: '13px',
 fontWeight: 600,
 color: AUDIT.accentInk,
 borderRadius: AUDIT.radiusSm,
 _hover: { bg: AUDIT.accentSoft, opacity: 1 },
};

/**
 * Cabeçalho ÚNICO da auditoria (EG-63). Junta o que era o header do AuditView +
 * o sub-header de cada renderer de tipo. Identidade vem por prop; progresso e
 * ações (download, ver como respondente) vêm do `headerData` que o renderer
 * publica no contexto AuditMode.
 */
export default function AuditHeader({
 name,
 type,
 companyName,
 currentStatus,
 hasStatusControl,
 onBack,
 backLoading,
 onEditStatus,
 showMasterClosedNote,
 showCompanyTurnNote,
 headerData,
}) {
 const hd = headerData || {};
 const hasProgress = hd.total > 0 || hd.requiredTotal > 0 || hd.indicatorsTotal > 0;

 return (
  <Box
   position='sticky'
   top='0'
   zIndex={20}
   bg={AUDIT.surface}
   borderBottom={`1px solid ${AUDIT.line}`}
   px={['16px', '28px']}
   pt='16px'
   pb='14px'
   mb={AUDIT.gapCard}
   borderRadius={AUDIT.radius}
   boxShadow={AUDIT.shadow}
  >
   <Button
    onClick={onBack}
    isLoading={backLoading}
    loadingText='Salvando…'
    leftIcon={<Box as={FiArrowLeft} boxSize='15px' />}
    variant='ghost'
    size='sm'
    h='28px'
    px='6px'
    ml='-6px'
    mb='8px'
    fontSize='13px'
    fontWeight={600}
    color={AUDIT.inkMuted}
    _hover={{ bg: AUDIT.tagNeutralBg, opacity: 1 }}
   >
    Voltar para a lista
   </Button>

   <Flex justify='space-between' align='flex-start' gap='20px' wrap='wrap'>
    <Box minW='240px'>
     <Text fontSize='20px' fontWeight={700} color={AUDIT.ink} lineHeight='1.2'>
      {companyName}
     </Text>
     <Flex align='center' gap='8px' mt='6px' wrap='wrap'>
      <Text fontSize='14px' color={AUDIT.inkMuted}>
       {name}
      </Text>
      <Box boxSize='3px' borderRadius='full' bg={AUDIT.lineStrong} />
      <Text sx={AUDIT_LABEL}>{TYPE_LABELS[type] || type}</Text>
      {currentStatus && (
       <>
        <Box boxSize='3px' borderRadius='full' bg={AUDIT.lineStrong} />
        <StatusTag status={currentStatus} />
       </>
      )}
     </Flex>
    </Box>

    <Flex gap='4px' wrap='wrap' justify='flex-end'>
     {hd.onViewAsRespondent && (
      <Button
       {...actionBtn}
       leftIcon={<Box as={FiEye} boxSize='14px' />}
       onClick={hd.onViewAsRespondent}
      >
       Ver como respondente
      </Button>
     )}
     {hd.onDownloadResponses && (
      <Button
       {...actionBtn}
       leftIcon={<Box as={FiFileText} boxSize='14px' />}
       onClick={hd.onDownloadResponses}
       isLoading={hd.downloadingResponses}
      >
       Baixar respostas
      </Button>
     )}
     {hd.onDownloadEvidence && (
      <Button
       {...actionBtn}
       leftIcon={<Box as={FiDownload} boxSize='14px' />}
       onClick={hd.onDownloadEvidence}
       isLoading={hd.downloadingEvidence}
       isDisabled={hd.evidenceDisabled}
      >
       Baixar evidências
      </Button>
     )}
     {hasStatusControl && onEditStatus && (
      <Button
       {...actionBtn}
       leftIcon={<Box as={FiEdit3} boxSize='14px' />}
       onClick={onEditStatus}
      >
       Editar status
      </Button>
     )}
    </Flex>
   </Flex>

   {hasProgress && (
    <Flex gap='24px' mt='16px' wrap='wrap'>
     {hd.total > 0 && (
      <Meter label='Respondidas' value={hd.answered || 0} total={hd.total} />
     )}
     {hd.requiredTotal > 0 && (
      <Meter
       label='Obrigatórias'
       value={hd.requiredAnswered || 0}
       total={hd.requiredTotal}
       color={AUDIT.statusAttention}
      />
     )}
     {hd.indicatorsTotal > 0 && (
      <Meter
       label='Indicadores GRI'
       value={hd.indicatorsAnswered || 0}
       total={hd.indicatorsTotal}
       color={AUDIT.tagGriInk}
      />
     )}
    </Flex>
   )}

   {showMasterClosedNote && (
    <Banner icon={FiInfo} tone='info'>
     Questionário auditado — como MASTER você pode corrigir as notas sem alterar
     o status.
    </Banner>
   )}
   {showCompanyTurnNote && (
    <Banner icon={FiLock} tone='attention'>
     A vez é da empresa neste momento — notas e medidas dos indicadores GRI ficam
     bloqueadas até o retorno.
    </Banner>
   )}
  </Box>
 );
}
